import React, { createContext, useReducer } from 'react';
import { simpleConfigReducer } from './SimpleConfigReducer';

const initialValue = {
    loading: true,
    defaultNavigationBarOrientation: "",
    defaultEnableDashboard: false,
    defaultEnableReporting: false,
    isMenuItemsTabLoadingData: false,
    entitiesLoaded: false,
    entities: [],
    selectedTabIndex: 0,
    portal: {},
    navigationBarOrientation: "",
    enableDashboard: false,
    enableReporting: false,
    isWorkflowEnabled: false,
    enableHistoryReporting: true,
    defaultEnableHistoryReporting: true,
    enableCommentReporting: true,
    defaultEnableCommentReporting: true,
    draggedItem: {},
    draggedItemIndex: -1,
    draggedOverItem: {},
    draggedOverItemIndex: -1,
    showEditAttributeDialog: false,
    showEditLabelTranslationDialog: false,
    selectedEntity: {},
    selectedRecentRecordId: "",
    selectedAttributes: [],
    entityAttributesLoading: true,
    entityAttributes: [],
    saving: false,
    publishing: false,
    saveAndPublishCompleted: false,
    publishSuccessful: true,
    publishMessage: "",
    portalTarget: {},
};

export const SimpleConfigContext = createContext({ ...initialValue });

export const SimpleConfigProvider = ({ children }) => {
    const [state, dispatch] = useReducer(simpleConfigReducer, { ...initialValue });

    const setSelectedTab = (e) => {
        dispatch({
            type: "CHANGE_TAB",
            payload: e.selected
        })
    }

    const setPortal = (portal) => {
        dispatch({
            type: "PORTAL_FETCHED",
            payload: portal
        })
    }

    const setPortalTarget = (target) => {
        dispatch({
            type: "TARGET_FETCHED",
            payload: target
        })
    }

    const setNavigationBarOrientation = (event) => {
        dispatch({
            type: "ORIENTATION_CHANGE",
            payload: event.target.value
        })
    }

    const setEnableDashboard = (event) => {
        dispatch({
            type: "ENABLE_DASHBOARD_CHANGE",
            payload: event.target.value
        })
    }

    const setEnableReporting = (event) => {
        dispatch({
            type: "ENABLE_REPORTING_CHANGE",
            payload: event.target.value
        })
    }

    const setEntities = (entities) => {
        dispatch({
            type: "ENTITIES_FETCHED",
            payload: entities.map((entity, index) => ({
                ...entity,
                defaultLabel: entity.label,
                defaultShow: entity.show,
                defaultIndex: index,
                defaultRecentRecordAttributes: [...entity.recentRecordAttributes],
                defaultLanguages: entity.languages,
                currentIndex: index,
                isChanged: false,
            })),
        });
    }

    const startDrag = (draggedItem, draggedItemIndex) => {
        dispatch({
            type: "DRAG_START",
            payload: { draggedItem, draggedItemIndex },
        });
    };

    const handleDraggedOverItem = (draggedOverItem, draggedOverItemIndex) => {
        dispatch({
            type: "DRAGGED_OVER",
            payload: { draggedOverItem, draggedOverItemIndex }
        })
    }

    const menuItemsDataLoaded = () => {
        dispatch({
            type: "SET_MENU_ITEM_DATA_LOADED"
        })
    }

    const updateEntityLabelAtIndex = (index, value) => {
        dispatch({
            type: "UPDATE_ENTITY_LABEL",
            payload: { index, value }
        })
    }

    const updateEntityShowAtIndex = (index, value) => {
        dispatch({
            type: "UPDATE_ENTITY_SHOW",
            payload: { index, value },
        });
    };

    const handleDragEnd = (reorderedData) => {
        dispatch({
            type: "DRAG_END",
            payload: reorderedData
        })
    }

    const toggleEditAttributeDialog = (value) => {
        dispatch({
            type: "TOGGLE_EDIT_ATTRIBUTE_DIALOG",
            payload: value,
        });
    };

    const toggleEditLabelTranslationDialog = () => {
        dispatch({
            type: "TOGGLE_EDIT_LABEL_TRANSLATION_DIALOG"
        });
    }

    const setSelectedEntity = (entity) => {
        dispatch({
            type: "SET_SELECTED_ENTITY",
            payload: entity
        })
    }

    const updateEntityLanguages= (data) => {
        debugger;
        dispatch({
            type: "UPDATE_ENTITY_LANGUAGES",
            payload: data
        })
    }

    const setEntityAttributes = (attributes) => {
        dispatch({
            type: "ENTITY_ATTRIBUTES_LOADED",
            payload: attributes,
        });
    };

    const saveAttributes = (payload) => {
        dispatch({
            type: "SAVE_ATTRIBUTES",
            payload
        })
    }

    const updateAttribute = (index, value) => {
        dispatch({
            type: "UPDATE_ATTRIBUTE",
            payload: { index, value }
        })
    }

    const updateDefaultValuesToCurrentValue = (newValues) => {
        dispatch({
            type: "SET_NEW_DEFAULT_VALUES",
            payload: newValues
        })
    }

    const showSaving = () => {
        dispatch({
            type: "START_SAVING"
        })
    }

    const showPublishing = () => {
        dispatch({
            type: "START_PUBLISHING"
        })
    }

    const showAfterPublishMessage = (status, message) => {
        dispatch({
            type: "PUBLISH_END",
            payload: { status, message }
        })
    }

    const hideAfterPublishMessage = () => {
        dispatch({
            type: "HIDE_AFTER_PUBLISH_MESSAGE"
        })
    }

    const revertChanges = () => {
        dispatch({
            type: "REVERT_CHANGES"
        })
    }

    const setLoading = value => dispatch({
        type: "SET_LOADING",
        payload: value
    })

    const setIsWorkflowEnabled = (value) => {
        dispatch({
            type: "SET_IS_WORKFLOW_ENABLED",
            payload: value
        })
    }

    const setEnableHistoryReporting = (event) => {
        dispatch({
            type: "ENABLE_HISTORY_REPORTING_CHANGE",
            payload: event.target.value
        })
    }

    const setEnableCommentReporting = (event) => {
        dispatch({
            type: "ENABLE_COMMENT_REPORTING_CHANGE",
            payload: event.target.value
        })
    }

    return (
        <SimpleConfigContext.Provider
            value={{
                ...state,
                setSelectedTab,
                setPortal,
                setNavigationBarOrientation,
                setEnableDashboard,
                setEnableReporting,
                setEntities,
                startDrag,
                handleDraggedOverItem,
                handleDragEnd,
                menuItemsDataLoaded,
                updateEntityLabelAtIndex,
                updateEntityShowAtIndex,
                toggleEditAttributeDialog,
                toggleEditLabelTranslationDialog,
                setSelectedEntity,
                updateEntityLanguages,
                setEntityAttributes,
                saveAttributes,
                updateAttribute,
                updateDefaultValuesToCurrentValue,
                showSaving,
                showPublishing,
                showAfterPublishMessage,
                setPortalTarget,
                hideAfterPublishMessage,
                revertChanges,
                setLoading,
                setIsWorkflowEnabled,
                setEnableHistoryReporting,
                setEnableCommentReporting
            }}
        >
            {children}
        </SimpleConfigContext.Provider>
    );
}